import { BriefcaseBusiness } from 'lucide-react';
import { useState } from 'react';

import {
  careersJobPath,
  enterStep,
  locationTypeFilters,
  locationTypeLabels,
} from '@/modules/careers/careers';
import { CareersJobRow } from '@/modules/careers/components/careersJobRow/CareersJobRow';
import { CareersLayout } from '@/modules/careers/components/careersLayout/CareersLayout';
import type { CareersPageApi, LocationType } from '@/modules/careers/types';
import { PortalEmptyState } from '@/modules/hiring-manager/components/portalEmptyState/PortalEmptyState';
import { SegmentedControl } from '@/modules/ui/components/segmented-control/SegmentedControl';

type RoleFilter = LocationType | 'all';

interface CareersPageViewProps {
  page: CareersPageApi;
  companySlug: string;
  /** Rise the page in; set when it replaces the loading skeleton. */
  animateEnter?: boolean;
}

/** A company's Careers Page: a short pitch, then one row per job that is Open for Applications. */
export function CareersPageView({
  page,
  companySlug,
  animateEnter = false,
}: CareersPageViewProps) {
  const [filter, setFilter] = useState<RoleFilter>('all');
  const roleCount = page.jobs.length;
  const filters = locationTypeFilters(page.jobs);
  const jobs =
    filter === 'all'
      ? page.jobs
      : page.jobs.filter((job) => job.locationTypes.includes(filter));

  return (
    <CareersLayout
      animateEnter={animateEnter}
      company={{ name: page.companyName, slug: companySlug }}
      description={`Open roles at ${page.companyName}. Apply in a few minutes.`}
      title={`Careers at ${page.companyName}`}
    >
      <header className="flex flex-col items-start gap-5 pt-8 pb-10 md:pt-16 md:pb-14">
        {roleCount > 0 && (
          <p
            {...enterStep(0)}
            className="bg-secondary-150 text-primary flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold"
          >
            <span aria-hidden className="bg-primary size-1.5 rounded-full" />
            We’re hiring · {roleCount} open {roleCount === 1 ? 'role' : 'roles'}
          </p>
        )}
        <h1
          {...enterStep(1)}
          className="max-w-[16ch] text-[40px] leading-[1.05] font-semibold tracking-[-0.035em] text-balance md:text-[56px]"
        >
          Join the team at {page.companyName}.
        </h1>
        <p
          {...enterStep(2)}
          className="text-muted-foreground max-w-[560px] text-base leading-relaxed md:text-[17px]"
        >
          {roleCount === 0
            ? 'There are no open roles right now.'
            : 'Apply in about two minutes. No CV needed: your LinkedIn profile is enough.'}
        </p>
      </header>

      <section aria-label="Open roles" className="flex flex-col gap-5">
        {filters.length > 0 && (
          <div {...enterStep(3)} className="flex">
            <SegmentedControl
              aria-label="Filter roles by work arrangement"
              className="h-auto max-w-full self-start overflow-x-auto"
              itemClassName="h-8 shrink-0"
              onValueChange={setFilter}
              options={[
                {
                  value: 'all',
                  label: <FilterLabel count={roleCount} label="All roles" />,
                },
                ...filters.map(({ type, count }) => ({
                  value: type,
                  label: (
                    <FilterLabel
                      count={count}
                      label={locationTypeLabels[type]}
                    />
                  ),
                })),
              ]}
              value={filter}
            />
          </div>
        )}
        {roleCount === 0 ? (
          <div {...enterStep(3)}>
            <PortalEmptyState
              icon={BriefcaseBusiness}
              size="row"
              title="Check back later for new openings."
            />
          </div>
        ) : (
          <ul className="divide-border divide-y border-y">
            {jobs.map((job, index) => (
              <CareersJobRow
                enterAt={4 + index}
                job={job}
                key={job.id}
                to={careersJobPath(companySlug, job.id)}
              />
            ))}
          </ul>
        )}
        {roleCount > 0 && (
          <p
            {...enterStep(4 + jobs.length)}
            className="text-muted-foreground pt-6 text-sm"
          >
            Don’t see a fit? New roles appear here as they open.
          </p>
        )}
      </section>
    </CareersLayout>
  );
}

function FilterLabel({ label, count }: { label: string; count: number }) {
  return (
    <span className="flex items-center gap-1.5">
      {label}
      <span className="text-muted-foreground text-xs tabular-nums">
        {count}
      </span>
    </span>
  );
}
